import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCriteriaMatches, useRelevanceWatches, useStreamSummary, useXReviews } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { EvidenceDisclosure } from 'src/components/editorial/EvidenceDisclosure';
import { Badge } from 'src/components/ui/Badge';
import { Disclosure } from 'src/components/ui/Disclosure';
import { Icon } from 'src/components/ui/Icon';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { RelevanceCurve } from 'src/components/editorial/RelevanceCurve';
import { ScoreBar } from 'src/components/ui/ScoreBar';
import { Skeleton } from 'src/components/ui/Skeleton';
import { formatDateTime } from 'src/lib/time';
import {
  CLASSIFICATION_BANDS,
  CONFIDENCE_LABEL,
  CONFIDENCE_MEANING,
  DIMENSION_ORDER,
  DIMENSION_RUBRIC,
  GATE_EXPLANATION,
  GRADE_MAX,
  PENALTY_EXCLUSIVE_GROUPS,
  PENALTY_FLOOR,
  PENALTY_RUBRIC,
  bandForScore,
  dimensionContribution,
  isKnownDimension,
  isKnownPenalty,
} from 'src/lib/relevance';
import { SOCIAL_CLASSIFICATION_LABEL, SOCIAL_CLASSIFICATION_STEP } from 'src/lib/tone';
import type {
  EditorialCriteriaMatch,
  EditorialRelevanceWatch,
  EditorialXPenalty,
  EditorialXReview,
  EditorialXReviewPayload,
  XDimension,
} from 'src/types/editorial';

const POST_METRICS_FORMAT = new Intl.NumberFormat('es-MX', { notation: 'compact' });

type XReviewPost = NonNullable<EditorialXReviewPayload['representative_posts']>[number];

type CriteriaGroupData = {
  name: string;
  matched: EditorialCriteriaMatch[];
  unmatched: EditorialCriteriaMatch[];
  best: EditorialCriteriaMatch;
};

/**
 * Answers: why was this promoted and that ignored?
 *
 * Composition §6 — never a bare score. Every row decomposes into the layers that produced it, and
 * a layer that did not run renders as null, not as zero. The composition groups blocks under their
 * criterion (the question is asked per criterion, not per block), ranks groups by their best
 * score, keeps the layer decomposition visible without opening anything, and demotes everything
 * that did not match to a collapsed quiet tail — §6: "why was this not promoted" is a real
 * editorial question, but it is not the first one.
 */
export function RelevanceWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [criteriaQuery, setCriteriaQuery] = useState('');
  const [criteriaSort, setCriteriaSort] = useState<'score' | 'name'>('score');
  const [xQuery, setXQuery] = useState('');
  const [xSort, setXSort] = useState<'score' | 'posts'>('score');
  const matches = useCriteriaMatches(streamId, !showUnmatched);
  const xReviews = useXReviews(streamId);
  // What each score did in the hours after it was taken. Its own request: a broadcast can have
  // curves for stories whose review was skipped, and reviews for stories never watched.
  const watches = useRelevanceWatches(streamId);
  // How many evaluations ran at all. Without it an empty table can only say "nothing here", which
  // reads as a stage that never ran — and the difference between "not evaluated" and "evaluated
  // and none reached its threshold" is the whole point of this screen.
  const summary = useStreamSummary(streamId);

  const { groups, unmatched } = useMemo(() => {
    const rows = sortMatches(matches.data?.criteriaMatches ?? []);
    return splitByCriterion(rows);
  }, [matches.data]);

  const criteriaNeedle = criteriaQuery.trim().toLowerCase();
  const criteriaFiltering = criteriaNeedle !== '';
  // The toggle refetches with the unmatched included; the filters only reshape what is already
  // here, so a filter change never triggers a request.
  const visibleGroups = useMemo(() => {
    const filtered = criteriaNeedle ? filterGroups(groups, criteriaNeedle) : groups;
    if (criteriaSort === 'name') return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    return filtered;
  }, [groups, criteriaNeedle, criteriaSort]);

  const xNeedle = xQuery.trim().toLowerCase();
  const xFiltering = xNeedle !== '';
  const visibleXReviews = useMemo(() => {
    if (!xReviews.data) return [];
    const searched = xNeedle
      ? xReviews.data.xReviews.filter(review => (review.payload.headline || review.storyId).toLowerCase().includes(xNeedle))
      : xReviews.data.xReviews;
    const ranked = sortXReviews(searched);
    if (xSort === 'posts') {
      return [...ranked].sort(
        (a, b) => (b.payload.representative_posts?.length ?? 0) - (a.payload.representative_posts?.length ?? 0)
      );
    }
    return ranked;
  }, [xReviews.data, xNeedle, xSort]);

  const unmatchedCount = showUnmatched ? countRows(unmatched) : 0;
  const representativePostCount = xReviews.data?.xReviews.reduce(
    (total, review) => total + (review.payload.representative_posts?.length ?? 0),
    0
  );

  // The section shell renders in every state — pending, error, empty — so the page never falls
  // back to a bare panel without its heading.
  return (
    <div className="page-wide">
      <section className="block" aria-labelledby="criteria-title">
        <header className="workspace-section-head">
          <div className="workspace-section-head__copy">
            <p className="workspace-section-head__eyebrow">01 <span aria-hidden="true">/</span> EVALUACIÓN EDITORIAL</p>
            <h2 id="criteria-title">Criterios editoriales</h2>
            <p>
              Qué bloques se relacionan con tus criterios. El resultado combina reglas, similitud semántica y juez;
              una raya indica que esa capa no se ejecutó.
            </p>
          </div>
          <div className="workspace-section-head__stats" aria-label="Resumen de evaluación editorial">
            <div className="workspace-section-head__stat">
              <strong>{matches.data ? countRows(visibleGroups) : '—'}</strong>
              <span>{criteriaFiltering ? 'resultados' : 'coincidencias'}</span>
            </div>
            <div className="workspace-section-head__stat">
              <strong>{summary.data?.summary.criteriaEvaluated ?? '—'}</strong>
              <span>bloques evaluados</span>
            </div>
          </div>
        </header>

        <section className="tx-filters" aria-label="Filtros de criterios">
          <div className="tx-field tx-search">
            <label htmlFor="criteria-q">Buscar</label>
            <div className="tx-search-box">
              <span className="tx-search-icon" aria-hidden="true">
                <Icon name="search" />
              </span>
              <input
                id="criteria-q"
                type="search"
                value={criteriaQuery}
                onChange={event => setCriteriaQuery(event.target.value)}
                placeholder="Buscar criterio o bloque"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="tx-field tx-field--select">
            <label htmlFor="criteria-sort">Ordenar</label>
            <select
              id="criteria-sort"
              value={criteriaSort}
              onChange={event => setCriteriaSort(event.target.value as 'score' | 'name')}
            >
              <option value="score">Por score</option>
              <option value="name">Por criterio</option>
            </select>
          </div>
          <label className="tx-check">
            <input type="checkbox" checked={showUnmatched} onChange={event => setShowUnmatched(event.target.checked)} />
            <span>Incluir los que no coincidieron</span>
          </label>
          <button
            className="tx-clear"
            type="button"
            onClick={() => {
              setCriteriaQuery('');
              setCriteriaSort('score');
            }}
            disabled={!criteriaFiltering && criteriaSort === 'score'}
          >
            Limpiar
          </button>
        </section>

        {matches.isPending ? (
          <Skeleton variant="rows" count={8} />
        ) : matches.isError ? (
          <LoadFailure title="No se pudo cargar la evaluación de relevancia" error={matches.error} onRetry={matches.refetch} />
        ) : groups.length === 0 && !showUnmatched ? (
          <EmptyCriteria
            evaluated={summary.data?.summary.criteriaEvaluated ?? 0}
            showingUnmatched={showUnmatched}
            onShowUnmatched={() => setShowUnmatched(true)}
          />
        ) : groups.length === 0 ? (
          <p className="relevance__note">
            Ninguna de las {summary.data?.summary.criteriaEvaluated ?? countRows(unmatched)} evaluaciones alcanzó su
            umbral. Todas están en <strong>Los que no coincidieron</strong>, abajo.
          </p>
        ) : visibleGroups.length === 0 ? (
          <p className="guion-empty">Ningún criterio coincide con los filtros.</p>
        ) : (
          <div className="criteria-groups">
            {visibleGroups.map(group => (
              <CriteriaGroup key={group.name} group={group} />
            ))}
          </div>
        )}

        {/* §6: unmatched criteria live in the quiet tail, collapsed, available. Only rendered once
            the editor asked for them — the default view stays ranked by what promoted. */}
        {showUnmatched && !matches.isPending && !matches.isError && unmatchedCount > 0 && (
          <section className="criteria-tail">
            <Disclosure summary="Los que no coincidieron" count={unmatchedCount}>
              <p className="relevance__note">
                “¿Por qué esto no se promovió?” es una pregunta editorial legítima, pero no es la primera. Se
                responde aquí, en voz baja: mismas capas, mismos scores, umbral no alcanzado.
              </p>
              {unmatched.map(group => (
                <CriteriaGroup key={group.name} group={group} quiet />
              ))}
            </Disclosure>
          </section>
        )}
      </section>

      <section className="relevance__social block" aria-labelledby="x-title">
        <header className="workspace-section-head" data-tone="social">
          <div className="workspace-section-head__copy">
            <p className="workspace-section-head__eyebrow">02 <span aria-hidden="true">/</span> ATENCIÓN SOCIAL</p>
            <h2 id="x-title">Relevancia en X</h2>
            <p>
              Estima cuánta atención recibe cada historia en X; no mide veracidad. El score combina seis dimensiones,
              penalizaciones y reglas de clasificación.
            </p>
          </div>
          <div className="workspace-section-head__stats" aria-label="Resumen de relevancia en X">
            <div className="workspace-section-head__stat">
              <strong>{xReviews.data ? xFiltering ? visibleXReviews.length : xReviews.data.xReviews.length : '—'}</strong>
              <span>{xFiltering ? 'resultados' : 'historias'}</span>
            </div>
            <div className="workspace-section-head__stat">
              <strong>{representativePostCount ?? '—'}</strong>
              <span>publicaciones analizadas</span>
            </div>
          </div>
        </header>

        <section className="tx-filters" aria-label="Filtros de historias de X">
          <div className="tx-field tx-search">
            <label htmlFor="x-q">Buscar</label>
            <div className="tx-search-box">
              <span className="tx-search-icon" aria-hidden="true">
                <Icon name="search" />
              </span>
              <input
                id="x-q"
                type="search"
                value={xQuery}
                onChange={event => setXQuery(event.target.value)}
                placeholder="Buscar historia"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="tx-field tx-field--select">
            <label htmlFor="x-sort">Ordenar</label>
            <select id="x-sort" value={xSort} onChange={event => setXSort(event.target.value as 'score' | 'posts')}>
              <option value="score">Por score</option>
              <option value="posts">Por publicaciones</option>
            </select>
          </div>
          <button
            className="tx-clear"
            type="button"
            onClick={() => {
              setXQuery('');
              setXSort('score');
            }}
            disabled={!xFiltering && xSort === 'score'}
          >
            Limpiar
          </button>
        </section>

        <SocialRubricReference />

        {xReviews.isPending && <Skeleton variant="rows" count={3} />}
        {xReviews.isError && (
          <LoadFailure title="No se pudo cargar la revisión de X" error={xReviews.error} onRetry={xReviews.refetch} />
        )}
        {xReviews.data && xReviews.data.xReviews.length === 0 && (
          <AbsenceState
            kind="skipped"
            what="No hay revisiones de X."
            why="X Social Review se omite si falta XAI_API_KEY o si la historia no llega al piso de especificidad."
          />
        )}
        {xReviews.data && xReviews.data.xReviews.length > 0 && visibleXReviews.length === 0 && (
          <p className="guion-empty">Ninguna historia coincide con los filtros.</p>
        )}
        {visibleXReviews.map(review => (
          <XReviewRow key={review.storyId} review={review} watch={watchForStory(watches.data?.watches ?? [], review.storyId)} />
        ))}
      </section>
    </div>
  );
}

/**
 * The watch belonging to one review.
 *
 * By `storyId` first and then by `sourceStoryIds`, because a clustered review's id is not any one
 * prenota's — the watch is opened against the cluster and a review row can be either side of it.
 */
function watchForStory(watches: Array<EditorialRelevanceWatch>, storyId: string): EditorialRelevanceWatch | undefined {
  return watches.find(watch => watch.storyId === storyId) ?? watches.find(watch => watch.sourceStoryIds.includes(storyId));
}

// ── criteria ────────────────────────────────────────────────────────────────

/**
 * One criterion, its blocks ranked inside it. The editorial question is asked per criterion
 * ("¿qué pasó con seguridad pública?"), so the criterion is the unit of scanning and the block
 * rows answer it. Groups rank by their best score — a criterion whose best block almost matched
 * outranks one whose blocks never came close.
 */
function splitByCriterion(rows: EditorialCriteriaMatch[]): { groups: CriteriaGroupData[]; unmatched: CriteriaGroupData[] } {
  const byName = new Map<string, { matched: EditorialCriteriaMatch[]; unmatched: EditorialCriteriaMatch[] }>();
  for (const row of rows) {
    const name = row.criterionName || row.criterionId;
    const entry = byName.get(name) ?? { matched: [], unmatched: [] };
    (row.matched ? entry.matched : entry.unmatched).push(row);
    byName.set(name, entry);
  }

  const build = ([name, entry]: [string, { matched: EditorialCriteriaMatch[]; unmatched: EditorialCriteriaMatch[] }]): CriteriaGroupData[] => {
    const matched = sortMatches(entry.matched);
    const unmatched = sortMatches(entry.unmatched);
    if (matched.length === 0 && unmatched.length === 0) return [];
    const best = matched[0] ?? unmatched[0];
    if (!best) return [];
    return [{ name, matched, unmatched, best }];
  };

  const rank = (a: CriteriaGroupData, b: CriteriaGroupData) => {
    const delta = b.best.finalScore - a.best.finalScore;
    if (delta !== 0) return delta;
    return a.name.localeCompare(b.name);
  };

  const groups = Array.from(byName.entries()).flatMap(build).filter(group => group.matched.length > 0).sort(rank);
  const unmatchedGroups = Array.from(byName.entries()).flatMap(build).filter(group => group.unmatched.length > 0).sort(rank);
  return { groups, unmatched: unmatchedGroups };
}

function countRows(groups: CriteriaGroupData[]): number {
  return groups.reduce((sum, group) => sum + group.matched.length + group.unmatched.length, 0);
}

/**
 * The toolbar's search, reshaping what is already loaded — never a new request. A hit on the
 * criterion name keeps the whole group; otherwise only the blocks whose id or action matches stay.
 */
function filterGroups(groups: CriteriaGroupData[], needle: string): CriteriaGroupData[] {
  return groups
    .map(group => {
      if (group.name.toLowerCase().includes(needle)) return group;
      const keep = (row: EditorialCriteriaMatch) =>
        row.chunkId.toLowerCase().includes(needle) || row.recommendedAction.toLowerCase().includes(needle);
      return { ...group, matched: group.matched.filter(keep), unmatched: group.unmatched.filter(keep) };
    })
    .filter(group => group.matched.length + group.unmatched.length > 0);
}

function CriteriaGroup({ group, quiet = false }: { group: CriteriaGroupData; quiet?: boolean }) {
  const rows = quiet ? group.unmatched : group.matched;

  return (
    <section className="criteria-group" data-matched={!quiet && group.matched.length > 0}>
      <header className="criteria-group__head">
        <h3 className="criteria-group__name">
          {group.name}
          <span className="criteria-group__count">{rows.length} {rows.length === 1 ? 'bloque' : 'bloques'}</span>
        </h3>
        <span className="criteria-group__meter" role="meter" aria-label={`Mejor score en ${group.name}`} aria-valuenow={group.best.finalScore} aria-valuemin={0} aria-valuemax={1}>
          <span className="criteria-group__meter-fill" style={{ inlineSize: `${Math.max(0, Math.min(group.best.finalScore, 1)) * 100}%` }} />
        </span>
        <span className="criteria-group__best">{group.best.finalScore.toFixed(2)}</span>
        <Badge tone={group.best.matched ? 'supported' : 'unresolved'}>{group.best.recommendedAction}</Badge>
      </header>
      <ul className="criteria-group__rows">
        {rows.map(match => (
          <li key={`${match.chunkId}-${match.criterionId}`}>
            <CriteriaRow match={match} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function CriteriaRow({ match }: { match: EditorialCriteriaMatch }) {
  return (
    <article className="criteria__row" data-matched={match.matched}>
      <header className="criteria__head">
        <span className="criteria__chunk mono">bloque {match.chunkId.split('_').at(-1)}</span>
        <span className="criteria__final">{match.finalScore.toFixed(2)}</span>
        <Badge tone={match.matched ? 'supported' : 'unresolved'}>{match.recommendedAction}</Badge>
      </header>

      {/* Composition §6 at scan level: the three layers that produced the number, always visible.
          A null layer is drawn as null, never as a zero bar. */}
      <div className="criteria__layers criteria__layers--inline">
        <ScoreBar label="reglas" value={match.rulesScore} />
        <ScoreBar
          label="semántico"
          value={match.semanticScore}
          nullNote="embeddings desactivadas"
        />
        <ScoreBar
          label="juez"
          value={match.judgeScore}
          nullNote={
            match.thresholds
              ? `no alcanzó ${match.thresholds.judgeTriggersAt.toFixed(2)} para disparar al juez`
              : 'no alcanzó el umbral del juez'
          }
        />
      </div>

      <Disclosure summary="Cómo se calculó">
        <CriteriaArithmetic match={match} />

        {/* Judgment — interface face. The quotes below it are testimony. */}
        {match.reason && <p className="criteria__reason">{match.reason}</p>}
        <EvidenceDisclosure evidence={match.evidence} label="Citas que lo sustentan" />
      </Disclosure>
    </article>
  );
}

/**
 * Spells out the average and the comparison that produced the action. Weights and thresholds are
 * persisted per match, so a row evaluated months ago explains itself with the configuration that
 * produced it — not with today's. Rows written before that were persisted say so plainly.
 */
function CriteriaArithmetic({ match }: { match: EditorialCriteriaMatch }) {
  const { weights, thresholds } = match;

  if (!weights || !thresholds) {
    return (
      <p className="criteria__math criteria__math--absent">
        Esta evaluación se guardó antes de que el pipeline registrara sus pesos y umbrales, así que no se puede
        reconstruir la aritmética exacta. Las evaluaciones nuevas sí la traen.
      </p>
    );
  }

  const terms: Array<{ label: string; weight: number; score: number }> = [
    { label: 'reglas', weight: weights.rules, score: match.rulesScore },
  ];
  if (weights.semantic !== null && match.semanticScore !== null) {
    terms.push({ label: 'semántico', weight: weights.semantic, score: match.semanticScore });
  }
  if (weights.judge !== null && match.judgeScore !== null) {
    terms.push({ label: 'juez', weight: weights.judge, score: match.judgeScore });
  }

  const totalWeight = terms.reduce((sum, term) => sum + term.weight, 0);
  const formula = terms.map(term => `${term.weight.toFixed(2)}×${term.score.toFixed(2)}`).join(' + ');

  return (
    <div className="criteria__math">
      <p className="mono criteria__math-formula">
        ({formula}) ÷ {totalWeight.toFixed(2)} = <strong>{match.finalScore.toFixed(2)}</strong>
      </p>
      <p>
        Los pesos de las capas que no corrieron se excluyen del divisor, para que una capa apagada no arrastre el
        score hacia abajo.
      </p>
      <ul className="criteria__math-gates">
        <li>
          <strong>{match.finalScore.toFixed(2)}</strong> {match.finalScore >= thresholds.matchedAt ? '≥' : '<'}{' '}
          {thresholds.matchedAt.toFixed(2)} (umbral de coincidencia) →{' '}
          {match.matched ? 'coincide' : 'no coincide'}
        </li>
        <li>
          <strong>{match.finalScore.toFixed(2)}</strong> {match.finalScore >= thresholds.alertAt ? '≥' : '<'}{' '}
          {thresholds.alertAt.toFixed(2)} (umbral de alerta) →{' '}
          {match.finalScore >= thresholds.alertAt ? 'alerta' : 'sin alerta'}
        </li>
        <li>
          Acción recomendada: <strong>{match.recommendedAction}</strong>
        </li>
      </ul>
    </div>
  );
}

// ── X social review ─────────────────────────────────────────────────────────

/** The rubric itself, stated once above the rows rather than repeated inside each one. */
/**
 * Nothing in the table, and why.
 *
 * A stream with six evaluations that all scored below their threshold is not a stream where the
 * stage did not run, and saying "no hay evaluaciones" over it is simply wrong — the tab counted
 * them, so the editor already knows they exist.
 */
function EmptyCriteria({
  evaluated,
  showingUnmatched,
  onShowUnmatched,
}: {
  evaluated: number;
  showingUnmatched: boolean;
  onShowUnmatched: () => void;
}) {
  if (evaluated === 0) {
    return (
      <AbsenceState
        kind="not_reached"
        what="No se evaluó ningún criterio."
        why="Los criterios se evalúan bloque por bloque mientras corre la etapa de temas."
      />
    );
  }

  if (showingUnmatched) {
    return (
      <AbsenceState
        kind="empty"
        what={`Se evaluaron ${evaluated} bloques contra los criterios y no hay ninguno que mostrar.`}
        why="Los resultados existen pero llegaron vacíos."
      />
    );
  }

  return (
    <section className="relevance-zero-state" aria-labelledby="relevance-zero-title">
      <div className="relevance-zero-state__metric" aria-label={`Cero coincidencias de ${evaluated} evaluaciones`}>
        <strong>0</strong>
        <span>de {evaluated}</span>
        <small>coincidencias</small>
      </div>
      <div className="relevance-zero-state__copy">
        <span className="relevance-zero-state__status"><span aria-hidden="true" /> Evaluación completa</span>
        <h3 id="relevance-zero-title">Ningún bloque superó el umbral</h3>
        <p>
          Se revisaron {evaluated} evaluaciones y ninguna llegó al score necesario para coincidir. Consulta sus
          puntuaciones para ver cuáles se acercaron más.
        </p>
        <button className="btn btn--secondary relevance-zero-state__action" type="button" onClick={onShowUnmatched}>
          <Icon name="tune" />
          Ver scores sin coincidencia
          <Icon name="chevronRight" />
        </button>
      </div>
    </section>
  );
}

function SocialRubricReference() {
  return (
    <Disclosure summary="Qué significa cada dimensión y cuánto pesa">
      <div className="rubric-reference">
        <div className="rubric-reference__heading">
          <div>
            <h3>Cómo se calcula</h3>
            <p>Seis señales componen el puntaje; cada una aporta una parte distinta del total.</p>
          </div>
          <span className="rubric-reference__total">100 <small>puntos posibles</small></span>
        </div>

        <ul className="rubric-reference__grid">
          {DIMENSION_ORDER.map(dimension => {
            const rubric = DIMENSION_RUBRIC[dimension];
            return (
              <li className="rubric-reference__card" key={dimension}>
                <header className="rubric-reference__card-head">
                  <h4>{rubric.label}</h4>
                  <span className="rubric-reference__weight"><strong>{rubric.weight}</strong> pts</span>
                </header>
                <span
                  className="rubric-reference__weight-track"
                  role="meter"
                  aria-label={`Peso de ${rubric.label}`}
                  aria-valuenow={rubric.weight}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span style={{ inlineSize: `${rubric.weight}%` }} />
                </span>
                <p className="rubric-reference__measure">{rubric.measures}</p>
                <Disclosure summary="Ver escala de 0 a 4" count={rubric.grades.length}>
                  <ol className="rubric-reference__grades">
                    {rubric.grades.map((meaning, grade) => (
                      <li key={meaning}>
                        <span>{grade}</span>
                        <p>{meaning}</p>
                      </li>
                    ))}
                  </ol>
                </Disclosure>
              </li>
            );
          })}
        </ul>

        <p className="rubric-reference__formula">
          <strong>Fórmula</strong>
          <span>(calificación ÷ {GRADE_MAX}) × peso de la dimensión</span>
          <span>Después se restan penalizaciones, hasta {Math.abs(PENALTY_FLOOR)} puntos.</span>
        </p>

        <Disclosure summary="Penalizaciones y clasificación" count={Object.keys(PENALTY_RUBRIC).length}>
          <ul className="rubric-reference__penalties">
            {Object.entries(PENALTY_RUBRIC).map(([type, rubric]) => (
              <li key={type}>
                <div className="rubric-reference__penalty-head">
                  <strong>{rubric.label}</strong>
                  <span>{rubric.points} pts</span>
                </div>
                <p>{rubric.meaning}</p>
              </li>
            ))}
          </ul>
          <p className="rubric-reference__rules">
            Los pares {PENALTY_EXCLUSIVE_GROUPS.map(group => group.map(type => PENALTY_RUBRIC[type].label).join(' o ')).join('; ')}
            {' '}son excluyentes: solo se aplica la penalización más severa. Las bandas son{' '}
            {CLASSIFICATION_BANDS.map(band => `${SOCIAL_CLASSIFICATION_LABEL[band.classification]} ≥ ${band.min}`).join(' · ')};
            {' '}las reglas finales pueden bajar la clasificación, nunca subirla.
          </p>
        </Disclosure>
      </div>
    </Disclosure>
  );
}

function XReviewRow({ review, watch }: { review: EditorialXReview; watch: EditorialRelevanceWatch | undefined }) {
  const payload = review.payload;

  // Design System §7: branching on the presence of `social_review_status` is the correct check —
  // a scored review has `overall_score` and no status field at all.
  if (payload.social_review_status !== undefined) {
    return (
      <AbsenceState
        kind={payload.social_review_status === 'skipped' ? 'skipped' : 'failed'}
        what={`${payload.headline || review.storyId}`}
        why={payload.skip_reason || payload.error || payload.failure_type || 'sin detalle'}
        {...(payload.retryable !== undefined
          ? { wouldChange: payload.retryable ? 'Es reintentable.' : 'No es reintentable.' }
          : {})}
      />
    );
  }

  const classification = payload.classification;
  const score = payload.overall_score;

  return (
    <article className="x-review" data-step={classification ? SOCIAL_CLASSIFICATION_STEP[classification] : 1}>
      <header className="x-review__head">
        <span className="x-review__headline">{payload.headline || review.storyId}</span>
        <Badge tone="social">{classification ? SOCIAL_CLASSIFICATION_LABEL[classification] : 'sin clasificar'}</Badge>
      <span className="x-review__score">
        {score ?? '—'}
        {score !== undefined && <small>/ 100</small>}
      </span>
      </header>

      {/* Composition §6 — the score is never bare. The meter reads at a glance; the disclosure
          below it says how the number was reached. */}
      {score !== undefined && (
        <span
          className="x-review__meter"
          role="meter"
          aria-label={`Relevancia social: ${score} de 100`}
          aria-valuenow={score}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span className="x-review__meter-fill" style={{ inlineSize: `${Math.max(0, Math.min(score, 100))}%` }} />
        </span>
      )}

      <XReviewSummaryLine payload={payload} />

      {score !== undefined && (
        <Disclosure summary="Cómo se llegó al score">
          {payload.base_score !== undefined && (
            <div className="score-math">
              <span className="score-math__term">
                <span className="score-math__label">base</span>
                <span className="score-math__value">{payload.base_score}</span>
                <span className="score-math__hint">suma de las seis dimensiones</span>
              </span>
              <span className="score-math__op" aria-hidden="true">
                {(payload.penalty_score ?? 0) < 0 ? '−' : '+'}
              </span>
              <span className="score-math__term">
                <span className="score-math__label">penalización</span>
                <span className="score-math__value">{Math.abs(payload.penalty_score ?? 0)}</span>
                <span className="score-math__hint">{payload.penalty_score ? `tope ${PENALTY_FLOOR}` : 'ninguna'}</span>
              </span>
              <span className="score-math__op" aria-hidden="true">
                =
              </span>
              <span className="score-math__term score-math__term--final">
                <span className="score-math__label">final</span>
                <span className="score-math__value">{score}</span>
              </span>
            </div>
          )}

          <PenaltyList penalties={payload.penalties_detected ?? []} />
          <GateList gatesApplied={payload.gates_applied ?? []} rawScore={score} classification={classification} />
          <ConfidenceNote confidence={payload.confidence} />
        </Disclosure>
      )}

      {/* Before the dimensions, because "did this hold up" is the question an editor asks of a
          score before "how was it built". The grades explain one reading; the curve says whether
          that reading survived the afternoon. */}
      {watch && (
        <Disclosure summary="Cómo evolucionó la relevancia" count={watch.samples.length}>
          <RelevanceCurve watch={watch} />
        </Disclosure>
      )}

      {payload.dimensions && <DimensionList dimensions={payload.dimensions} />}

      {payload.representative_posts && payload.representative_posts.length > 0 && (
        <Disclosure summary="Publicaciones representativas" count={payload.representative_posts.length}>
          <ul className="x-review__posts">
            {payload.representative_posts.map(post => (
              <li key={post.post_id} className="x-post">
                <header className="x-post__head">
                  <span className="x-post__account">{post.account}</span>
                  <span className="x-post__handle mono">{post.account_handle}</span>
                  <span className="x-post__date">{formatDateTime(post.published_at)}</span>
                </header>
                <p className="x-post__text">{post.text_excerpt}</p>
                {post.why_relevant && <p className="x-post__why">{post.why_relevant}</p>}
                <footer className="x-post__meta">
                  <span className="x-post__stance">
                    {post.stance}
                    {post.stance_target ? ` · ${post.stance_target}` : ''}
                  </span>
                  {formatPostMetrics(post.visible_metrics)}
                  <a className="x-post__link" href={post.url} target="_blank" rel="noreferrer">
                    ver en X →
                  </a>
                </footer>
              </li>
            ))}
          </ul>
        </Disclosure>
      )}
      {payload.dominant_narratives && payload.dominant_narratives.length > 0 && (
        <Disclosure summary="Narrativas dominantes" count={payload.dominant_narratives.length}>
          <ul className="x-review__narratives">
            {payload.dominant_narratives.map(narrative => (
              <li key={narrative.narrative}>
                <span className="mono">{narrative.prevalence}</span> {narrative.narrative}
              </li>
            ))}
          </ul>
        </Disclosure>
      )}
      {payload.limitations && payload.limitations.length > 0 && (
        <Disclosure summary="Limitaciones" count={payload.limitations.length}>
          <ul className="limitation-list">
            {payload.limitations.map(limitation => (
              <li key={limitation}>{limitation}</li>
            ))}
          </ul>
        </Disclosure>
      )}
    </article>
  );
}

/** Ventana analizada, confianza y cuántas cuentas sostienen la lectura — el contexto mínimo para
 *  saber si el número merece atención antes de abrir nada. */
function XReviewSummaryLine({ payload }: { payload: EditorialXReviewPayload }) {
  const window = payload.analysis_window;
  const accounts = new Set((payload.representative_posts ?? []).map(post => post.account_handle).filter(Boolean)).size;

  return (
    <p className="x-review__summary">
      {window && (
        <span>
          Ventana {window.from_date} → {window.to_date}
        </span>
      )}
      <span>
        {payload.representative_posts?.length ?? 0} publicaciones · {accounts} cuentas distintas
      </span>
      {payload.confidence && <span>{CONFIDENCE_LABEL[payload.confidence]}</span>}
      {payload.technical_usage?.model && <span className="mono">{payload.technical_usage.model}</span>}
    </p>
  );
}

function DimensionList({ dimensions }: { dimensions: NonNullable<EditorialXReviewPayload['dimensions']> }) {
  const graded = DIMENSION_ORDER.flatMap(dimension => {
    const value = dimensions[dimension];
    return value ? [{ dimension, value }] : [];
  });

  // A dimension the pipeline emitted that this build has no rubric for still has to appear —
  // silence would read as "not graded".
  const unknown = Object.entries(dimensions).filter(([name]) => !isKnownDimension(name));

  return (
    <Disclosure summary="Dimensiones" count={graded.length + unknown.length}>
      {/* Tiles, not rows: each dimension is read as a unit — name, grade, what the grade means. */}
      <ul className="rubric__dimensions rubric__dimensions--grid">
        {graded.map(({ dimension, value }) => (
          <li key={dimension} className="dimension">
            <span className="dimension__head">
              <span className="dimension__label">{DIMENSION_RUBRIC[dimension].label}</span>
              <span className="dimension__grade mono">
                {value.grade} / {GRADE_MAX}
              </span>
            </span>
            <span
              className="dimension__dots"
              role="img"
              aria-label={`Calificación ${value.grade} de ${GRADE_MAX}`}
            >
              {Array.from({ length: GRADE_MAX }, (_, dot) => (
                <span key={dot} className="dimension__dot" data-on={dot < value.grade} />
              ))}
            </span>
            <span className="dimension__contribution">
              {dimensionContribution(dimension, value.grade).toFixed(1)} de {DIMENSION_RUBRIC[dimension].weight} pts
            </span>
            <span className="rubric__grade-meaning">{gradeMeaning(dimension, value.grade)}</span>
            {value.reason && <span className="dimension__reason">{value.reason}</span>}
          </li>
        ))}
      </ul>
      {unknown.length > 0 && (
        <ul className="rubric__dimensions">
          {unknown.map(([name, value]) => (
            <li key={name}>
              <ScoreBar label={name} value={value?.grade ?? null} scale={4} nullNote="sin calificación" />
              <p className="rubric__grade-meaning">Dimensión nueva del pipeline, sin descripción en esta versión.</p>
            </li>
          ))}
        </ul>
      )}
    </Disclosure>
  );
}

function gradeMeaning(dimension: XDimension, grade: number): string {
  return DIMENSION_RUBRIC[dimension].grades[grade] ?? `Calificación ${grade} fuera del rango 0–${GRADE_MAX}.`;
}

function PenaltyList({ penalties }: { penalties: EditorialXPenalty[] }) {
  if (penalties.length === 0) {
    return <p className="criteria__reason">No se detectó ninguna penalización.</p>;
  }

  return (
    <>
      <h4 className="rubric__subhead">Penalizaciones detectadas</h4>
      <ul className="penalty-list">
        {penalties.map(penalty => (
          <li key={penalty.type} className="penalty">
            <span className="penalty__head">
              <strong>{isKnownPenalty(penalty.type) ? PENALTY_RUBRIC[penalty.type].label : penalty.type}</strong>
              {isKnownPenalty(penalty.type) && (
                <span className="penalty__points mono">{PENALTY_RUBRIC[penalty.type].points}</span>
              )}
            </span>
            <span className="penalty__meaning">
              {isKnownPenalty(penalty.type) ? PENALTY_RUBRIC[penalty.type].meaning : 'Penalización nueva del pipeline.'}
            </span>
            {penalty.reason && <span className="penalty__reason">{penalty.reason}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * A gate is the answer to "scored 84, why does it say Relevante". Shows the band the raw score
 * earned, then each rule that took it down.
 */
function GateList({
  gatesApplied,
  rawScore,
  classification,
}: {
  gatesApplied: string[];
  rawScore: number;
  classification: EditorialXReviewPayload['classification'];
}) {
  if (gatesApplied.length === 0) return null;

  const band = bandForScore(rawScore);

  return (
    <>
      <h4 className="rubric__subhead">Reglas que bajaron la clasificación</h4>
      <p className="criteria__reason">
        Por puntaje le tocaba <strong>{SOCIAL_CLASSIFICATION_LABEL[band]}</strong>
        {classification && classification !== band && (
          <> y quedó en <strong>{SOCIAL_CLASSIFICATION_LABEL[classification]}</strong></>
        )}
        .
      </p>
      <ul className="gate-rules">
        {gatesApplied.map(gate => (
          <li key={gate}>
            <span className="mono">{gate}</span>
            <span className="rubric__grade-meaning">
              {GATE_EXPLANATION[gate] ?? 'Regla nueva del pipeline, sin descripción en esta versión.'}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

function ConfidenceNote({ confidence }: { confidence: EditorialXReviewPayload['confidence'] }) {
  if (!confidence) return null;

  return (
    <p className="confidence-note">
      <strong>{CONFIDENCE_LABEL[confidence]}.</strong> {CONFIDENCE_MEANING[confidence]} La confianza no cambia el
      puntaje; dice cuánta evidencia lo sostiene.
    </p>
  );
}

/** Mirrors the legacy `x_review_dashboard.html` metric row — only the counters that exist. */
function formatPostMetrics(metrics: XReviewPost['visible_metrics']) {
  if (!metrics) return null;
  const entries: Array<[string, number | undefined]> = [
    ['♥', metrics.likes],
    ['↻', metrics.reposts],
    ['💬', metrics.replies],
    ['👁', metrics.views],
  ];
  const present = entries.filter((entry): entry is [string, number] => entry[1] !== undefined);
  if (present.length === 0) return null;

  return (
    <span className="x-post__metrics">
      {present.map(([icon, value]) => (
        <span key={icon} className="x-post__metric">
          {icon} {POST_METRICS_FORMAT.format(value)}
        </span>
      ))}
    </span>
  );
}

/** Highest score first, stable on (chunk, criterion) so two loads agree (Composition §13). */
function sortMatches(matches: EditorialCriteriaMatch[]): EditorialCriteriaMatch[] {
  return [...matches].sort((a, b) => {
    const delta = b.finalScore - a.finalScore;
    if (delta !== 0) return delta;
    return `${a.chunkId}${a.criterionId}`.localeCompare(`${b.chunkId}${b.criterionId}`);
  });
}

/**
 * Scored reviews first, highest score on top — the same question the criteria region answers
 * ("why this and not that"). Skipped and failed reviews have no score to rank by and keep their
 * arrival order at the tail, where their AbsenceState already explains them.
 */
function sortXReviews(reviews: EditorialXReview[]): EditorialXReview[] {
  return [...reviews].sort((a, b) => {
    const scoreA = a.payload.overall_score;
    const scoreB = b.payload.overall_score;
    if (scoreA === undefined && scoreB === undefined) return 0;
    if (scoreA === undefined) return 1;
    if (scoreB === undefined) return -1;
    return scoreB - scoreA;
  });
}
